// Shared email/markdown renderer — used by TicketDetail and DevTickets so both
// panels render message bodies identically (HTML emails + plain-text/markdown).
import { memo, useMemo } from "react";

// Route external image URLs through our server-side proxy so CSP doesn't block them
function proxyExternalImages(html) {
  return html.replace(
    /(<img\b[^>]*?\ssrc=)(["'])(https?:\/\/[^"']+)\2/gi,
    (_, tag, q, src) => `${tag}${q}/api/proxy/image?url=${encodeURIComponent(src)}${q}`
  );
}

// A body is HTML only when it contains a real formatting tag. Plain-text mail is full of angle brackets
// that are not tags — "From: Johnny Palumbo <johnny@jpalumbo.com>", "<no subject>", "<https://…>" — and the
// old test (any "<letter…>") rendered those as HTML: every line break collapsed into one paragraph and the
// bracketed text vanished (TKT-26422's Send-to-Dev note, 2026-10-23: 303 messages in 120 days were hit,
// 284 of them by an email address; no real HTML email changes classification).
const HTML_TAG = /<\/?(?:html|head|body|meta|style|title|p|br|div|span|font|b|i|u|s|strong|em|a|img|table|thead|tbody|tfoot|tr|td|th|ul|ol|li|h[1-6]|blockquote|pre|code|hr|center|section|article|header|footer|small|sup|sub|o:p)(?=[\s>\/])[^>]*>/i;
const looksLikeHtml = (s) => HTML_TAG.test(String(s || ""));

// Detect HTML vs plain text and render appropriately.
// React 19 re-applies dangerouslySetInnerHTML whenever it is handed a NEW {__html} object (it
// no longer compares the strings), so building that object inline rewrote the email on every
// render of the page around it. Each keystroke in the reply box re-created the email's images,
// and a broken one (the browser never caches a failure) went back to /api/proxy/image every
// time — about 2 requests per keystroke, enough to trip nginx's 60/min per-IP limit, after which
// the dashboard and tickets came back empty or "not found" for everyone at that office
// (Oct 20, 21 and 27, 2026). Memoised, so the DOM is only touched when the text changes.
const HtmlContent = memo(function HtmlContent({ text }) {
  // Normalize line endings before any rendering
  const normalized = text ? text.replace(/\r\n/g, "\n").replace(/\r/g, "\n") : "";
  const isHtml = looksLikeHtml(normalized);
  const html = useMemo(() => (isHtml ? { __html: proxyExternalImages(normalized) } : null), [isHtml, normalized]);
  if (!text) return null;
  if (html) {
    return (
      <div className="email-html-content" dangerouslySetInnerHTML={html} />
    );
  }
  // Plain text — decode entities before passing to RichText
  const decoded = normalized
    .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'");
  return <RichText text={decoded} />;
});

function RichText({ text }) {
  if (!text) return null;

  // Detect image URLs
  const imageExts = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;
  const urlRegex  = /(https?:\/\/[^\s]+)/g;

  const renderLine = (line, idx) => {
    // Heading
    if (line.startsWith("### ")) return <h3 key={idx} style={{ color:"var(--t-text-primary)", fontSize:14, fontWeight:700, margin:"12px 0 4px" }}>{line.slice(4)}</h3>;
    if (line.startsWith("## "))  return <h2 key={idx} style={{ color:"var(--t-text-primary)", fontSize:15, fontWeight:700, margin:"14px 0 4px" }}>{line.slice(3)}</h2>;
    if (line.startsWith("# "))   return <h1 key={idx} style={{ color:"var(--t-text-primary)", fontSize:16, fontWeight:700, margin:"14px 0 6px" }}>{line.slice(2)}</h1>;

    // Bullet point
    if (line.startsWith("- ") || line.startsWith("* ")) {
      return (
        <div key={idx} style={{ display:"flex", gap:8, margin:"3px 0", paddingLeft:8 }}>
          <span style={{ color:"#60a5fa", flexShrink:0, marginTop:1 }}>•</span>
          <span>{renderInline(line.slice(2))}</span>
        </div>
      );
    }

    // Numbered list
    const numMatch = line.match(/^(\d+)\.\s(.+)/);
    if (numMatch) {
      return (
        <div key={idx} style={{ display:"flex", gap:8, margin:"3px 0", paddingLeft:8 }}>
          <span style={{ color:"#60a5fa", flexShrink:0, minWidth:16 }}>{numMatch[1]}.</span>
          <span>{renderInline(numMatch[2])}</span>
        </div>
      );
    }

    // Horizontal rule
    if (line.trim() === "---" || line.trim() === "***") {
      return <hr key={idx} style={{ border:"none", borderTop:"1px solid var(--t-border-soft)", margin:"10px 0" }}/>;
    }

    // Empty line — spacing
    if (!line.trim()) return <div key={idx} style={{ height:8 }} />;

    // Check if line is purely an image URL
    const trimmed = line.trim();
    if (urlRegex.test(trimmed) && imageExts.test(trimmed)) {
      return (
        <div key={idx} style={{ margin:"8px 0" }}>
          <img src={trimmed} alt="attachment" style={{ maxWidth:"100%", maxHeight:300, borderRadius:8, border:"1px solid rgba(148,163,184,0.18)", display:"block" }}
            onError={e => { e.target.style.display="none"; }}/>
        </div>
      );
    }

    // Normal paragraph line
    return <p key={idx} style={{ margin:"4px 0", lineHeight:1.7 }}>{renderInline(line)}</p>;
  };

  const renderInline = (text) => {
    // Split by URLs and bold markers
    const parts = [];
    let last = 0;
    const combined = /(\*\*(.+?)\*\*|`(.+?)`|(https?:\/\/[^\s]+))/g;
    let match;
    while ((match = combined.exec(text)) !== null) {
      if (match.index > last) parts.push({ type:"text", val:text.slice(last, match.index) });
      if (match[1].startsWith("**")) {
        parts.push({ type:"bold", val:match[2] });
      } else if (match[1].startsWith("`")) {
        parts.push({ type:"code", val:match[3] });
      } else {
        const url = match[4];
        if (imageExts.test(url)) {
          parts.push({ type:"img", val:url });
        } else {
          parts.push({ type:"link", val:url });
        }
      }
      last = match.index + match[0].length;
    }
    if (last < text.length) parts.push({ type:"text", val:text.slice(last) });

    return parts.map((p, i) => {
      if (p.type === "bold") return <strong key={i} style={{ color:"var(--t-text-primary)", fontWeight:700 }}>{p.val}</strong>;
      if (p.type === "code") return <code key={i} style={{ background:"var(--t-input)", color:"var(--badge-violet)", padding:"1px 6px", borderRadius:4, fontSize:12, fontFamily:"monospace" }}>{p.val}</code>;
      if (p.type === "img")  return <img key={i} src={p.val} alt="inline" style={{ maxWidth:"100%", maxHeight:200, borderRadius:6, verticalAlign:"middle", border:"1px solid rgba(148,163,184,0.18)" }} onError={e=>e.target.style.display="none"}/>;
      if (p.type === "link") return <a key={i} href={p.val} target="_blank" rel="noreferrer" style={{ color:"#60a5fa", wordBreak:"break-all" }}>{p.val}</a>;
      return <span key={i}>{p.val}</span>;
    });
  };

  const lines = text.split("\n");
  return (
    <div style={{ color:"var(--t-text-body)", fontSize:13, lineHeight:1.7 }}>
      {lines.map((line, i) => renderLine(line, i))}
    </div>
  );
}

export { HtmlContent, proxyExternalImages, RichText, looksLikeHtml };
